import { formatCurrency, formatNumber, formatPercent } from "@/components/deal-metrics/shared";
import { formatLongMonthLabel, formatMonthLabel } from "@/components/executive/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { GOAL_TEXT, type GoalStatus, goalStatus } from "@/lib/goal-status";
import { cn } from "@/lib/utils";

export interface MonthlyRow {
  month: string;
  opportunities: number;
  closedDeals: number;
  errorCount: number;
  errorRate: number;
  grossSales: number;
  netSales: number;
  grossProfitMargin: number;
  closeRate: number;
  avgOrderValue: number;
}

type MetricFormat = "count" | "currency" | "percent";

export type MetricKey = Exclude<keyof MonthlyRow, "month">;

export type GoalComparison = "gte" | "lte";

export type GoalsMap = Partial<Record<MetricKey, { value: number; comparison: GoalComparison }>>;

const METRIC_CONFIG: { key: MetricKey; label: string; format: MetricFormat }[] = [
  { key: "opportunities", label: "Total Opportunities", format: "count" },
  { key: "closedDeals", label: "Total Deals Closed", format: "count" },
  { key: "closeRate", label: "Close Rate", format: "percent" },
  { key: "grossSales", label: "Gross Sales", format: "currency" },
  { key: "netSales", label: "Total Net Sales", format: "currency" },
  { key: "avgOrderValue", label: "Average Order Value", format: "currency" },
  { key: "grossProfitMargin", label: "Gross Profit Margin", format: "percent" },
  { key: "errorRate", label: "Error Rate", format: "percent" },
];

function isGoalMet(goals: GoalsMap, key: MetricKey, actual: number): boolean | null {
  const goal = goals[key];
  if (!goal) return null;
  return goal.comparison === "gte" ? actual >= goal.value : actual <= goal.value;
}

/** Where a value sits against its goal — drives the green / amber / red colouring. */
function statusForGoal(
  goal: { value: number; comparison: GoalComparison } | undefined,
  actual: number | null,
): GoalStatus {
  if (!goal || actual === null) return "none";
  return goalStatus(actual, goal.value, goal.comparison);
}

/**
 * How much is left to reach the goal.
 * - "gte": positive amount still needed to hit the goal (0 once met).
 * - "lte": positive amount of remaining headroom before crossing the goal
 *   (e.g. how much error rate can still rise before hitting 1%); negative
 *   means it's already over.
 */
function computeRemaining(goals: GoalsMap, key: MetricKey, actual: number): number | null {
  const goal = goals[key];
  if (!goal) return null;
  return goal.comparison === "gte" ? Math.max(goal.value - actual, 0) : goal.value - actual;
}

/** Metrics that are totals summed across the year (vs. rates/averages). */
const SUM_METRIC_KEYS = new Set<MetricKey>([
  "opportunities",
  "closedDeals",
  "grossSales",
  "netSales",
]);

/**
 * The portion of an annual goal that applies to a single month.
 * - Totals (opportunities, closed deals, gross/net sales) are divided by 12.
 * - Rates and averages (close rate, error rate, avg order value, GPM) apply
 *   the same target every month.
 */
function getMonthlyGoal(
  goals: GoalsMap,
  key: MetricKey,
): { value: number; comparison: GoalComparison } | undefined {
  const goal = goals[key];
  if (!goal) return undefined;
  return SUM_METRIC_KEYS.has(key) ? { value: goal.value / 12, comparison: goal.comparison } : goal;
}

function isGoalMetForTarget(
  goal: { value: number; comparison: GoalComparison } | undefined,
  actual: number,
): boolean | null {
  if (!goal) return null;
  return goal.comparison === "gte" ? actual >= goal.value : actual <= goal.value;
}

function computeRemainingForTarget(
  goal: { value: number; comparison: GoalComparison } | undefined,
  actual: number,
): number | null {
  if (!goal) return null;
  return goal.comparison === "gte" ? Math.max(goal.value - actual, 0) : goal.value - actual;
}

function formatMetric(value: number, format: MetricFormat): string {
  switch (format) {
    case "count":
      return formatNumber(Math.round(value));
    case "currency":
      return formatCurrency(value);
    case "percent":
      return formatPercent(value);
  }
}

/**
 * Per-month value for a metric.
 * - grossProfitMargin: the cost-based margin returned by the API (gross profit ÷ gross sales).
 * - everything else: the raw field value.
 */
function getValue(key: MetricKey, row: MonthlyRow): number {
  return Number(row[key]);
}

/**
 * Total/average column for a metric.
 * - count/currency: sum across months.
 * - closeRate: weighted average (total closed deals ÷ total opportunities).
 * - errorRate: weighted average (total errors ÷ total closed deals).
 * - grossProfitMargin: total gross profit ÷ total gross sales (gross-sales-weighted).
 */
function computeTotal(key: MetricKey, rows: MonthlyRow[]): number {
  if (key === "closeRate") {
    const totalOpps = rows.reduce((sum, r) => sum + r.opportunities, 0);
    const totalClosed = rows.reduce((sum, r) => sum + r.closedDeals, 0);
    return totalOpps > 0 ? totalClosed / totalOpps : 0;
  }
  if (key === "errorRate") {
    const totalClosed = rows.reduce((sum, r) => sum + r.closedDeals, 0);
    const totalErrors = rows.reduce((sum, r) => sum + r.errorCount, 0);
    return totalClosed > 0 ? totalErrors / totalClosed : 0;
  }
  if (key === "avgOrderValue") {
    const totalClosed = rows.reduce((sum, r) => sum + r.closedDeals, 0);
    const totalGrossSales = rows.reduce((sum, r) => sum + r.grossSales, 0);
    return totalClosed > 0 ? totalGrossSales / totalClosed : 0;
  }
  if (key === "grossProfitMargin") {
    // Reconstruct gross-profit dollars per month (margin × gross sales) so the yearly
    // figure is correctly gross-sales-weighted rather than a naive average of ratios.
    const totalGrossSales = rows.reduce((sum, r) => sum + r.grossSales, 0);
    const totalGrossProfit = rows.reduce((sum, r) => sum + r.grossProfitMargin * r.grossSales, 0);
    return totalGrossSales > 0 ? totalGrossProfit / totalGrossSales : 0;
  }
  return rows.reduce((sum, r) => sum + Number(r[key]), 0);
}

/** Current month as "YYYY-MM". */
function currentMonthKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Year-end projection based on current pace: takes the fully completed
 * months so far this fiscal year (excluding the current, in-progress
 * month), scales their totals up to a full 12 months, then derives rate
 * metrics from those projected totals.
 * Returns null if no months have been completed yet.
 */
function computeProjection(rows: MonthlyRow[]): Record<MetricKey, number> | null {
  const current = currentMonthKey();
  const elapsed = rows.filter((r) => r.month < current);
  const monthsElapsed = elapsed.length;
  if (monthsElapsed === 0) return null;

  const scale = 12 / monthsElapsed;
  const pOpportunities = elapsed.reduce((sum, r) => sum + r.opportunities, 0) * scale;
  const pClosedDeals = elapsed.reduce((sum, r) => sum + r.closedDeals, 0) * scale;
  const pErrorCount = elapsed.reduce((sum, r) => sum + r.errorCount, 0) * scale;
  const pGrossSales = elapsed.reduce((sum, r) => sum + r.grossSales, 0) * scale;
  const pNetSales = elapsed.reduce((sum, r) => sum + r.netSales, 0) * scale;
  const pGrossProfit =
    elapsed.reduce((sum, r) => sum + r.grossProfitMargin * r.grossSales, 0) * scale;

  return {
    opportunities: pOpportunities,
    closedDeals: pClosedDeals,
    errorCount: pErrorCount,
    errorRate: pClosedDeals > 0 ? pErrorCount / pClosedDeals : 0,
    grossSales: pGrossSales,
    netSales: pNetSales,
    grossProfitMargin: pGrossSales > 0 ? pGrossProfit / pGrossSales : 0,
    closeRate: pOpportunities > 0 ? pClosedDeals / pOpportunities : 0,
    avgOrderValue: pClosedDeals > 0 ? pGrossSales / pClosedDeals : 0,
  };
}

/** Number of days elapsed so far this month, and total days in the month. */
function daysElapsedInMonth(): { elapsed: number; total: number } {
  const now = new Date();
  const total = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return { elapsed: now.getDate(), total };
}

/**
 * Month-end projection based on the current month's pace so far: scales
 * the month-to-date totals up by total days ÷ days elapsed, then derives
 * rate metrics from those projected totals.
 */
function computeMonthProjection(row: MonthlyRow): Record<MetricKey, number> {
  const { elapsed, total } = daysElapsedInMonth();
  const scale = elapsed > 0 ? total / elapsed : 1;

  const pOpportunities = row.opportunities * scale;
  const pClosedDeals = row.closedDeals * scale;
  const pErrorCount = row.errorCount * scale;
  const pGrossSales = row.grossSales * scale;
  const pNetSales = row.netSales * scale;

  return {
    opportunities: pOpportunities,
    closedDeals: pClosedDeals,
    errorCount: pErrorCount,
    errorRate: pClosedDeals > 0 ? pErrorCount / pClosedDeals : 0,
    grossSales: pGrossSales,
    netSales: pNetSales,
    // Margin is scale-invariant, so the projected month keeps the month's cost-based margin.
    grossProfitMargin: row.grossProfitMargin,
    closeRate: pOpportunities > 0 ? pClosedDeals / pOpportunities : 0,
    avgOrderValue: pClosedDeals > 0 ? pGrossSales / pClosedDeals : 0,
  };
}

interface MetricsTrackerTableProps {
  rows: MonthlyRow[];
  fiscalYear: number | null;
  goals: GoalsMap;
  title: string;
  subtitle: string;
}

/**
 * Renders the "Current Month Performance" card (when the current month has
 * data) plus the full monthly metrics table with Total/Avg, Projected
 * (Year-End), Annual Goal, and Remaining to Goal columns.
 */
export function MetricsTrackerTable({
  rows,
  fiscalYear,
  goals,
  title,
  subtitle,
}: MetricsTrackerTableProps) {
  const projectedRow = computeProjection(rows);
  const currentMonthRow = rows.find((r) => r.month === currentMonthKey());
  const monthProjection = currentMonthRow ? computeMonthProjection(currentMonthRow) : null;

  return (
    <div className="space-y-6">
      {currentMonthRow && (
        <Card>
          <CardHeader>
            <CardTitle>Current Month Performance</CardTitle>
            <p className="text-sm text-muted-foreground">
              {formatLongMonthLabel(currentMonthRow.month)} — {subtitle}
            </p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Metric</TableHead>
                  <TableHead className="text-right whitespace-nowrap font-semibold">
                    This Month
                  </TableHead>
                  <TableHead className="text-right whitespace-nowrap font-semibold">
                    Projected (Month-End)
                  </TableHead>
                  <TableHead className="text-right whitespace-nowrap font-semibold">
                    Monthly Goal
                  </TableHead>
                  <TableHead className="text-right whitespace-nowrap font-semibold">
                    Remaining to Goal
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {METRIC_CONFIG.map(({ key, label, format }) => {
                  const actual = getValue(key, currentMonthRow);
                  const monthlyGoal = getMonthlyGoal(goals, key);
                  const met = isGoalMetForTarget(monthlyGoal, actual);
                  const remaining = computeRemainingForTarget(monthlyGoal, actual);
                  const projected = monthProjection ? monthProjection[key] : null;
                  const projectedMet =
                    projected === null ? null : isGoalMetForTarget(monthlyGoal, projected);
                  const projectedStatus = statusForGoal(monthlyGoal, projected);
                  const metStatus = statusForGoal(monthlyGoal, actual);
                  return (
                    <TableRow key={key}>
                      <TableCell className="font-medium whitespace-nowrap">{label}</TableCell>
                      <TableCell className="text-right whitespace-nowrap font-semibold">
                        {formatMetric(actual, format)}
                      </TableCell>
                      <TableCell
                        className={cn("text-right whitespace-nowrap", GOAL_TEXT[projectedStatus])}
                      >
                        {projected === null ? "—" : formatMetric(projected, format)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap text-muted-foreground">
                        {monthlyGoal
                          ? `${monthlyGoal.comparison === "lte" ? "< " : ""}${formatMetric(monthlyGoal.value, format)}`
                          : "—"}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-right whitespace-nowrap font-semibold",
                          GOAL_TEXT[metStatus],
                        )}
                      >
                        {remaining === null
                          ? "—"
                          : met
                            ? "Goal met"
                            : monthlyGoal?.comparison === "lte"
                              ? `Over by ${formatMetric(Math.abs(remaining), format)}`
                              : formatMetric(remaining, format)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {fiscalYear && (
            <p className="text-sm text-muted-foreground">
              FY{String(fiscalYear - 1).slice(-2)}/{String(fiscalYear).slice(-2)} (Apr–Mar)
            </p>
          )}
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Metric</TableHead>
                {rows.map((r) => (
                  <TableHead key={r.month} className="text-right whitespace-nowrap">
                    {formatMonthLabel(r.month)}
                  </TableHead>
                ))}
                <TableHead className="text-right whitespace-nowrap font-semibold">
                  Total / Avg
                </TableHead>
                <TableHead className="text-right whitespace-nowrap font-semibold">
                  Projected (Year-End)
                </TableHead>
                <TableHead className="text-right whitespace-nowrap font-semibold">
                  Annual Goal
                </TableHead>
                <TableHead className="text-right whitespace-nowrap font-semibold">
                  Remaining to Goal
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {METRIC_CONFIG.map(({ key, label, format }) => {
                const total = computeTotal(key, rows);
                const goal = goals[key];
                const met = isGoalMet(goals, key, total);
                const remaining = computeRemaining(goals, key, total);
                const projected = projectedRow ? projectedRow[key] : null;
                const projectedMet = projected === null ? null : isGoalMet(goals, key, projected);
                const projectedStatus = statusForGoal(goal, projected);
                const metStatus = statusForGoal(goal, total);
                return (
                  <TableRow key={key}>
                    <TableCell className="font-medium whitespace-nowrap">{label}</TableCell>
                    {rows.map((r) => (
                      <TableCell key={r.month} className="text-right whitespace-nowrap">
                        {formatMetric(getValue(key, r), format)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right whitespace-nowrap font-semibold">
                      {formatMetric(total, format)}
                    </TableCell>
                    <TableCell
                      className={cn("text-right whitespace-nowrap", GOAL_TEXT[projectedStatus])}
                    >
                      {projected === null ? "—" : formatMetric(projected, format)}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap text-muted-foreground">
                      {goal
                        ? `${goal.comparison === "lte" ? "< " : ""}${formatMetric(goal.value, format)}`
                        : "—"}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right whitespace-nowrap font-semibold",
                        GOAL_TEXT[metStatus],
                      )}
                    >
                      {remaining === null
                        ? "—"
                        : met
                          ? "Goal met"
                          : goal?.comparison === "lte"
                            ? `Over by ${formatMetric(Math.abs(remaining), format)}`
                            : formatMetric(remaining, format)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
